import i18n from "@/i18n";
import { localeFor } from "@/utils/formatters";
import { ROLE_OPTIONS as roles } from "@/constants/roles";

export type ShiftCard = {
  id: string;
  name: string; // Shift Title
  roleLabel: string;
  roleValue: string;
  ratePerHour: number;
  total?: number;
  dateLabel: string;
  timeLabel: string;
  venue: string;
  roleIconValue: string;
  businessId?: number;
  businessUserId?: string;
  businessName?: string;
  businessAvatar?: string;
  businessRating?: number | null;
  businessRatingCount?: number | null;
  workerId?: string;
  workerName?: string;
  workerAvatar?: string;
  workerRating?: number | null;
  workerRatingCount?: number | null;
  description?: string;
  status?: string;
  startDateTime?: string;
  /** Worker view: a not-yet-booked shift (employer offer awaiting the worker, or the worker's
   *  application awaiting approval). Clicking opens the shift modal instead of the sidebar. */
  pendingStatus?: "offered" | "applied";
  shiftId?: number;
  days?: Array<string>;
  /** Days posted together as one multi-day block share this (each day is its own shift). */
  seriesId?: string | null;
  /** ISO date (yyyy-mm-dd) the shift is on. */
  startDate?: string;
};

/**
 * Whether a shift's start (date + time) is in the past. Used to gate editing —
 * shifts can only be edited before they start. Accepts an ISO-ish local
 * datetime string like "2026-07-01T09:00:00". Returns false when unknown so the
 * UI stays permissive (the backend is the authoritative guard).
 */
export function shiftHasStarted(startDateTime?: string | null): boolean {
  if (!startDateTime) return false;
  const start = new Date(startDateTime);
  if (Number.isNaN(start.getTime())) return false;
  return start.getTime() <= Date.now();
}

/**
 * For "pick a shift" lists: shows each multi-day series once (its earliest day, which then
 * opens a day picker) instead of one entry per day. `seriesCount` is the number of days.
 */
export function collapseSeries<
  T extends { series_id?: string | null; start_date?: string | null },
>(shifts: Array<T>): Array<T & { seriesCount: number }> {
  const out: Array<T & { seriesCount: number }> = [];
  const bySeries = new Map<string, T & { seriesCount: number }>();
  for (const s of shifts) {
    if (!s.series_id) {
      out.push({ ...s, seriesCount: 1 });
      continue;
    }
    const existing = bySeries.get(s.series_id);
    if (!existing) {
      const entry = { ...s, seriesCount: 1 };
      bySeries.set(s.series_id, entry);
      out.push(entry);
    } else {
      existing.seriesCount += 1;
      if ((s.start_date ?? "") < (existing.start_date ?? "")) {
        Object.assign(existing, s, { seriesCount: existing.seriesCount });
      }
    }
  }
  return out;
}

export function shiftToCard(
  shift: any,
  opts?: {
    id?: number;
    workerName?: string;
    workerId?: string;
    businessName?: string;
    dateLabel?: string;
    timeLabel?: string;
  },
): ShiftCard {
  // Normalize role value for icon usage and lookup
  const roleValue = shift.role_name
    .toLowerCase()
    .replace(/\s+/g, "_")
    .replace(/[^a-z0-9_]/g, "");

  // Lookup human-readable label
  const roleObj = roles.find((r: any) => r.value === roleValue);
  const roleLabel = roleObj ? roleObj.label : shift.role_name;

  // Calculate total pay for the shift
  let total: number | undefined = undefined;
  try {
    if (
      shift.start_date &&
      shift.start_time &&
      shift.end_date &&
      shift.end_time &&
      typeof shift.wage === "number"
    ) {
      const startTime = shift.start_time;
      const endTime = shift.end_time;

      // Calculate hours per shift
      const shiftStart = new Date(`${shift.start_date}T${startTime}`);
      const shiftEnd = new Date(`${shift.start_date}T${endTime}`);
      const shiftMs = shiftEnd.getTime() - shiftStart.getTime();
      const hoursPerShift = shiftMs / (1000 * 60 * 60);

      if (hoursPerShift > 0) {
        // Always show daily amount (not total for recurring shifts)
        total = hoursPerShift * shift.wage;
      }
    }
  } catch {}

  return {
    id: opts?.id !== undefined ? String(opts.id) : String(shift.id),
    name: shift.name || "",
    roleLabel,
    roleValue,
    ratePerHour: shift.wage,
    total,
    dateLabel:
      opts?.dateLabel ??
      (() => {
        if (!shift.start_date) return "";
        try {
          const start = new Date(shift.start_date + "T00:00:00");
          // Shifts are single-day now; only legacy multi-day shifts render a range.
          const end =
            shift.end_date && shift.end_date !== shift.start_date
              ? new Date(shift.end_date + "T00:00:00")
              : null;
          const startStr = start.toLocaleDateString(localeFor(i18n.language || "en"), {
            ...(end ? {} : { weekday: "short" as const }),
            month: "short",
            day: "numeric",
            year: "numeric",
          });
          let dateStr = startStr;
          if (end) {
            const endStr = end.toLocaleDateString(localeFor(i18n.language || "en"), {
              month: "short",
              day: "numeric",
              year: "numeric",
            });
            dateStr = `${startStr} – ${endStr}`;
          }

          return dateStr;
        } catch {
          return shift.start_date;
        }
      })(),
    timeLabel:
      opts?.timeLabel ??
      (() => {
        function toTimeString(t: any) {
          if (!t) return "";
          if (typeof t === "string") {
            // "09:00:00"
            const [hour, minute] = t.split(":");
            const date = new Date();
            date.setHours(Number(hour));
            date.setMinutes(Number(minute));
            return date.toLocaleTimeString(localeFor(i18n.language || "en"), {
              hour: "numeric",
              minute: "2-digit",
              hour12: true,
            });
          } else if (
            typeof t === "object" &&
            t.hour !== undefined &&
            t.minute !== undefined
          ) {
            const date = new Date();
            date.setHours(Number(t.hour));
            date.setMinutes(Number(t.minute));
            return date.toLocaleTimeString(localeFor(i18n.language || "en"), {
              hour: "numeric",
              minute: "2-digit",
              hour12: true,
            });
          }
          return "";
        }
        if (!shift.start_time || !shift.end_time) return "";
        return `${toTimeString(shift.start_time)} – ${toTimeString(shift.end_time)}`;
      })(),
    venue: shift.address.address1 || shift.address.location || "",
    businessId: shift.business.id,
    businessName: opts?.businessName || shift.business.name,
    businessAvatar: shift.business.avatar,
    businessRating: shift.business.overall_rating ?? null,
    businessRatingCount: shift.business.rating_count ?? null,
    workerId: opts?.workerId,
    workerName: opts?.workerName,
    workerAvatar: shift.worker?.avatar,
    workerRating: shift.worker?.overall_rating ?? null,
    workerRatingCount: shift.worker?.rating_count ?? null,
    description: shift.role_description,
    status: shift.status,
    roleIconValue: roleValue,
    startDateTime: shift.start_date
      ? `${shift.start_date}T${shift.start_time}`
      : undefined,
    days: shift.days,
    seriesId: shift.series_id ?? null,
    startDate: shift.start_date,
  };
}
